import { keepPreviousData, useQuery, UseQueryResult } from '@tanstack/react-query'
import { request } from 'lib/api/fetch-api'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  PaginationState,
  ColumnFiltersState,
} from '@tanstack/react-table'
import {
  SearchV2Response,
  SearchV2Ticket,
  SearchResponse,
  SearchTicket,
  SearchProject,
  SearchTicketFile,
  convertSearchTicketFilesToTicketFiles,
} from 'interfaces/ticket'
import { useState } from 'react'

import { TicketFile } from 'lib/api/ticket-files/ticket-files'

export const columnIds = {
  query: 'query',
  file_state: 'file_statuses[]',
  brand_ids: 'brand_ids[]',
  user_ids: 'user_ids[]',
  designer_ids: 'designer_ids[]',
  updated_date: 'updated_date',
  format: 'ticket_deliverable_formats[]',
  actions: 'actions',
  tag: 'company_tag_ids[]',
  project_ids: 'project_ids[]',
}

const getColumnDef = [
  {
    id: columnIds.query,
  },
  {
    id: columnIds.brand_ids,
  },
  {
    id: columnIds.project_ids,
  },
  {
    id: columnIds.tag,
  },
  {
    id: columnIds.file_state,
  },
  {
    id: columnIds.user_ids,
  },

  {
    id: columnIds.updated_date,
  },
  {
    id: columnIds.format,
  },
]

const DEFAULT_PAGE_SIZE = 8

export const useAssetsSearch = () => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  })

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  const assetsQuerySettings = {
    ...columnFilters.reduce((acc, filter) => {
      // Special handling for date filter
      if (filter.id === columnIds.updated_date && Array.isArray(filter.value)) {
        const [start, end] = filter.value
        if (start) acc['file_updated_at_gte'] = start
        if (end) acc['file_updated_at_lte'] = end
        return acc
      }

      if (filter.id === columnIds.format && Array.isArray(filter.value)) {
        const formats = filter.value
        if (formats) acc['formats[]'] = formats
        return acc
      }

      if (Array.isArray(filter.value)) {
        if (filter.value.length === 0) {
          return acc
        }
        acc[filter.id] = filter.value
        return acc
      }
      acc[filter.id] = filter.value
      return acc
    }, {}),
    sort_key: 'file_updated_date',
    sort_order: 'desc',
    'index[]': ['ticket_file'],
    page: String(pagination.pageIndex),
    page_size: pagination.pageSize,
  }

  const assetsQueryKey = ['search_assets', pagination, assetsQuerySettings]
  const originalAssetsQuery = useQuery({
    queryKey: assetsQueryKey,
    queryFn: async () => {
      const response = await request({
        endpoint: 'getExternalSearch',
        query: assetsQuerySettings,
      })

      return esToSearchResponseData(response) as UseQueryResult<SearchResponse<TicketFile>>['data']
    },
    placeholderData: keepPreviousData,
  })

  const table = useReactTable({
    columns: getColumnDef,
    data: originalAssetsQuery?.data?.data || [],
    state: {
      pagination,
      columnFilters,
    },
    maxMultiSortColCount: 1,
    onColumnFiltersChange: (updaterOrValue) => {
      setPagination({ pageIndex: 1, pageSize: DEFAULT_PAGE_SIZE })
      setColumnFilters(updaterOrValue)
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    onPaginationChange: setPagination,
    getSortedRowModel: getSortedRowModel(),
  })

  return {
    pagination,
    table,
    assetsQueryKey,
    assetsQuery: originalAssetsQuery,
    assetsLoading: originalAssetsQuery.isLoading,
    assetsCount: originalAssetsQuery?.data?.meta?.total,
    assets: originalAssetsQuery?.data?.data,
  }
}

function esToSearchResponseData(
  queryData: UseQueryResult<SearchV2Response<SearchV2Ticket | SearchProject | SearchTicketFile>>['data'],
): UseQueryResult<SearchResponse<SearchTicket | SearchProject | TicketFile>>['data'] {
  const sources = queryData?.hits?.hits?.map((hit) => hit._source) || []
  const meta = {
    total: queryData?.hits?.total?.value || 0,
    pageSize: DEFAULT_PAGE_SIZE,
    previousPage: null,
    nextPage: null,
  }

  const data = convertSearchTicketFilesToTicketFiles(sources as SearchTicketFile[])

  return {
    data,
    meta,
  } as UseQueryResult<SearchResponse<SearchTicket | SearchProject | TicketFile>>['data']
}
